import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { HeroSlide } from "@/types/content";

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  const go = (dir: number) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            title={`${slide.title} - 欲望之岛焦点推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-8 flex items-center">
        <div className="w-full max-w-2xl animate-fade-up" key={index}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-500 text-primary-950 text-xs font-label font-semibold">
            <i className="ri-fire-fill"></i>
            {slides[index].badge}
          </span>
          <h2 className="mt-4 text-3xl md:text-5xl font-heading font-bold text-background-50">
            {slides[index].title}
          </h2>
          <p className="mt-3 text-sm md:text-lg text-background-200 font-label">
            {slides[index].subtitle}
          </p>
          <p className="mt-2 text-xs md:text-base text-background-300 max-w-lg">
            {slides[index].tagline}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={slides[index].link}
              className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看详情
            </Link>
            <Link
              to="/category/movie"
              className="px-5 py-2.5 rounded-md bg-background-50/10 text-background-50 text-sm font-label border border-background-50/30 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/10 text-background-50 border border-background-50/30 hover:bg-background-50/20 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}